import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import Lending from './pages/Lending';
import Tracker from './pages/Tracker';
import Calculator from './pages/CalculatorPage';
import FinancialPlanningPage from './pages/FinancialPlanningPage';
import Contacts from './pages/Contacts';
import Questions from './pages/Questions';
import About from './pages/About';
import OfferPage from './pages/OfferPage';
import PrivacyPage from './pages/PrivacyPage';
import SecurityPage from './pages/SecurityPage';
import RefundPage from './pages/RefundPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RegisterPage from './pages/RegisterPage';
import VerificationPage from './pages/VerificationPage';
import RegisterLayout from './pages/RegisterLayout';
import ProfilePage from './pages/ProfilePage';
import PlansPage from './pages/PlansPage';
import PaymentResultPage from './pages/PaymentResultPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import LotsPage from './pages/LotsPage';
import LotDetailPage from './pages/LotDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { ProcurementAccessProvider } from './context/ProcurementAccessContext';
import ProtectedRoute from './components/ProtectedRoute';
import ServiceWorkspaceEntry from './components/ServiceWorkspaceEntry';
import GuestRoute from './components/GuestRoute';
import AdminRoute from './components/AdminRoute';
import ScrollToTop from './components/ScrollToTop';
import Seo from './components/Seo';
import Analytics from './components/Analytics';
import SupportChat from './components/SupportChat';
import ServicePage from './pages/ServicePage';
import NotFoundPage from './pages/NotFoundPage';

// Admin pages — lazy-loaded to keep the main bundle lean
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminExperience = lazy(() => import('./pages/admin/AdminExperience'));
const ExperiencePage = lazy(() => import('./pages/ExperiencePage'));
const AdminPromotions = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminProcurement = lazy(() => import('./pages/admin/AdminProcurement'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminTenders = lazy(() => import('./pages/admin/AdminTenders'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminServerStatus = lazy(() => import('./pages/admin/AdminServerStatus'));
const SiteSettingsLayout = lazy(() => import('./pages/admin/site-settings/SiteSettingsLayout'));
const DocumentsSettings = lazy(() => import('./pages/admin/site-settings/DocumentsSettings'));
const DocumentEditorPage = lazy(() => import('./pages/admin/site-settings/DocumentEditorPage'));
const NewsSettings = lazy(() => import('./pages/admin/site-settings/NewsSettings'));
const NewsEditorPage = lazy(() => import('./pages/admin/site-settings/NewsEditorPage'));
const NewsCategoriesSettings = lazy(() => import('./pages/admin/site-settings/NewsCategoriesSettings'));

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <ProcurementAccessProvider>
          <ScrollToTop />
          <Seo />
          <Analytics />
          <SupportChat />
          <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center" role="status" aria-label="Загрузка"><span className="h-8 w-8 animate-spin rounded-full border-2 border-goszakup-600 border-t-transparent" /></div>}>
          <Routes>
            {/* ── Auth routes ── */}
            <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
            <Route element={<GuestRoute><RegisterLayout /></GuestRoute>}>
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerificationPage />} />
            </Route>

            {/* ── Admin routes ── */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute requiredRole="manager">
                  <AdminLayout>
                    <AdminUsers />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/billing"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminBilling />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminSubscriptions />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminRoute requiredRole="superadmin">
                  <AdminLayout>
                    <AdminSettings />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route path="/admin/lots" element={<AdminRoute><AdminLayout><AdminProcurement service="lots" /></AdminLayout></AdminRoute>} />
            <Route path="/admin/suppliers" element={<AdminRoute><AdminLayout><AdminProcurement service="suppliers" /></AdminLayout></AdminRoute>} />
            <Route path="/admin/experience" element={<AdminRoute><AdminLayout><AdminExperience /></AdminLayout></AdminRoute>} />
            <Route
              path="/admin/tenders"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminTenders />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <AdminRoute requiredRole="superadmin">
                  <AdminLayout>
                    <AdminAuditLog />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/server-status"
              element={
                <AdminRoute requiredRole="superadmin">
                  <AdminLayout>
                    <AdminServerStatus />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/site-settings"
              element={
                <AdminRoute requiredRole="superadmin">
                  <AdminLayout>
                    <SiteSettingsLayout />
                  </AdminLayout>
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="documents" replace />} />
              <Route path="documents" element={<DocumentsSettings />} />
              <Route path="documents/:slug/edit" element={<DocumentEditorPage />} />
              <Route path="news" element={<NewsSettings />} />
              <Route path="news/:slug/edit" element={<NewsEditorPage />} />
              <Route path="news-categories" element={<NewsCategoriesSettings />} />
            </Route>

            {/* ── Main site routes ── */}
            <Route path="/admin/promotions" element={<AdminRoute requiredRole="superadmin"><AdminLayout><AdminPromotions /></AdminLayout></AdminRoute>} />
            <Route path="/*" element={
              <Navigation>
                <Routes>
                  <Route path="/" element={<Lending />} />
                  <Route path="/services/:service" element={<ServicePage />} />
                  <Route path="/tracker" element={<ServiceWorkspaceEntry key="tracker" kind="tracker"><Tracker /></ServiceWorkspaceEntry>} />
                  <Route path="/calculator" element={<ServiceWorkspaceEntry key="calculator" kind="calculator"><Calculator /></ServiceWorkspaceEntry>} />
                  <Route path="/financial-planning" element={<ServiceWorkspaceEntry key="planning" kind="planning"><FinancialPlanningPage /></ServiceWorkspaceEntry>} />
                  <Route path="/experience" element={<ExperiencePage />} />
                  <Route path="/plans" element={<PlansPage />} />
                  <Route path="/payment/success" element={<ProtectedRoute><PaymentResultPage expected="success" /></ProtectedRoute>} />
                  <Route path="/payment/failure" element={<ProtectedRoute><PaymentResultPage expected="failure" /></ProtectedRoute>} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/news/:slug" element={<NewsArticlePage />} />
                  <Route path="/lots" element={<LotsPage />} />
                  <Route path="/lots/:id" element={<LotDetailPage />} />
                  <Route path="/suppliers" element={<SuppliersPage />} />
                  <Route path="/suppliers/:bin" element={<SupplierDetailPage />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/questions" element={<Questions />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/offer" element={<OfferPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/security" element={<SecurityPage />} />
                  <Route path="/refund" element={<RefundPage />} />
                  <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Navigation>
            } />
          </Routes>
          </Suspense>
          </ProcurementAccessProvider>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
